// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import useProjectLabels from 'hooks/useProjectLabels';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import NoContentBackdrop from 'components/treactui-template/organisms/backdrop/NoContentBackdrop';
import { useTranslation } from 'react-i18next';
import { DatasetFilterType, isQCResultMatchingProcessTag, QCResult } from './data/filter/filters-defaults';
import DataGrid from './data/DataGrid';
import DataHeader from './data/DataHeader';
import DataTable from './data/DataTable';
import DataFooter from './data/DataFooter';
import { useParams } from 'react-router-dom';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import ParamsWithId from 'model/ParamsWithId';
import { useDataFilter } from './data/DataFilterProvider';
import { EmptyState } from '@tls/treact-ui';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';

export default function Data() {
  const { filter, setFilter, viewType, setViewType } = useDataFilter();
  const { labels, loading: labelsLoading } = useProjectLabels();
  const [projectTags, setProjectTags] = useState<string[]>([]);
  const { t } = useTranslation();

  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);
  const modelClassesWithoutBackground = useMemo(() => {
    return selectedProject?.projectUseCase?.modelClasses?.filter(modelClass => modelClass.classIndex !== 0);
  }, [selectedProject?.projectUseCase?.modelClasses]);

  useEffect(() => {
    labels.forEach(label => {
      if (label.tags && label.tags.length > 0) {
        setProjectTags(prev => [...new Set([...prev, ...label.tags])]);
      }
    });
  }, [labels]);

  const filteredLabels = useMemo(() => {
    let _filteredLabels = [...labels];

    if (filter.dataset.length > 0) {
      _filteredLabels = _filteredLabels.filter(
        label =>
          (!label.hasMask && filter.dataset.includes(DatasetFilterType.UnLabeled)) ||
          (!label.isValidation && label.hasMask && filter.dataset.includes(DatasetFilterType.TrainingSet)) ||
          (label.isValidation && label.hasMask && filter.dataset.includes(DatasetFilterType.EvaluationSet))
      );
    }

    if (filter.processTag.length > 0) {
      _filteredLabels = _filteredLabels.filter(label =>
        label.qcResult === undefined
          ? false
          : filter.processTag.some(tag => isQCResultMatchingProcessTag(label.qcResult ?? QCResult.Undefined, tag))
      );
    }

    if (filter.label.length > 0) {
      _filteredLabels = _filteredLabels.filter(label => {
        const matchingUsedLabels = label.classIndicesInMask.filter(usedLabel => filter.label.includes('' + usedLabel));
        return matchingUsedLabels.length > 0;
      });
    }

    if (filter.imageTag.length > 0) {
      _filteredLabels = _filteredLabels.filter(label =>
        label.tags ? label.tags.some(tag => filter.imageTag.includes(tag)) : false
      );
    }

    return _filteredLabels;
  }, [labels, filter]);

  return (
    <Root>
      <DataHeader
        filter={filter}
        setFilter={setFilter}
        viewType={viewType}
        setViewType={setViewType}
        projectTags={projectTags}
      />
      {(labelsLoading || !modelClassesWithoutBackground) && (
        <NoDataPlaceholder data-testid='data-loading-state'>
          <LoaderContainer>
            <TestableLoader label={t('project.data.loading')} data-testid='data-loading-indicator' />
          </LoaderContainer>
        </NoDataPlaceholder>
      )}
      {!labelsLoading && labels.length > 0 && filteredLabels.length === 0 && (
        <NoDataPlaceholder data-testid='data-filtered-empty-state'>
          <EmptyState iconType='noData' label={t('project.data.noData.title')} />
        </NoDataPlaceholder>
      )}
      {labels.length === 0 && !labelsLoading && (
        <NoDataPlaceholder data-testid='data-empty-state'>
          <NoContentBackdrop show title={t('project.data.noData.title')} details={t('project.data.noData.details')} />
        </NoDataPlaceholder>
      )}
      {!labelsLoading && filteredLabels.length > 0 && viewType === 'grid' && (
        <DataGrid
          filteredLabels={filteredLabels}
          projectTags={projectTags}
          modelClasses={modelClassesWithoutBackground ?? []}
        />
      )}
      {!labelsLoading && filteredLabels.length > 0 && viewType === 'table' && (
        <DataTable
          filteredLabels={filteredLabels}
          projectTags={projectTags}
          modelClasses={modelClassesWithoutBackground ?? []}
        />
      )}
      <DataFooter labels={labels} />
    </Root>
  );
}

const NoDataPlaceholder = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 1rem;
`;
const LoaderContainer = styled.div`
  max-width: 500px;
  width: 100%;
`;

const Root = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
